import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import logging from "../utils/logging";
import { Shield, AlertTriangle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = params.get("token");
    if (!token) {
      setError("No token provided.");
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/verify`, { token }, { withCredentials: true });
        setUser(res.data);
        navigate("/dashboard", { replace: true, state: { user: res.data } });
      } catch (err) {
        logging.error("Magic-link verify failed", err);
        const detail = err.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "This link is invalid or has expired.");
      }
    })();
  }, [navigate, params, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6] px-6" data-testid="verify-page">
      <div className="bg-white border border-gray-200 rounded-sm p-8 max-w-sm w-full text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <Shield className="w-8 h-8 text-black" strokeWidth={1.5} />
          <span className="font-heading text-2xl font-black tracking-tighter">GUARD</span>
        </div>
        {error ? (
          <>
            <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-3" strokeWidth={1.5} />
            <h1 className="font-heading text-lg font-bold text-gray-900 mb-2">Sign-in failed</h1>
            <p className="text-sm text-gray-600 font-body mb-4" data-testid="verify-error">{error}</p>
            <Link to="/login" className="inline-block bg-black text-white rounded-sm px-5 py-2 text-sm font-body" data-testid="back-to-login">
              Request a new link
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-gray-700 animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-600 font-body">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
